import {
  Button,
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@kmgestao/ui';

import { ProposalPublicView } from '../../../../features/proposals/ProposalPublicView.js';
import type {
  PlatformSettings,
  ProposalCopySettings,
} from '../../../../features/proposals/platform-settings.js';
import type { LeadProposalSummary } from '../../../../features/proposals/schemas.js';

import type { PlatformSettingsFormInput } from '../../../../features/platform-settings-admin/index.js';

/** Modal de prévia da proposta usando os valores ATUAIS do form de
 * `PlatformSettingsForm` — permite Karla ver esteticamente como a
 * proposta vai ficar ANTES de salvar. Usa dados fictícios de lead
 * + proposta combo (SaaS + consultoria) com preço custom = cenário
 * mais completo que exercita todos os blocos renderizados pelo
 * `ProposalPublicView`. */
export function PlatformSettingsPreviewDialog({
  open,
  onOpenChange,
  formValues,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Valores correntes do form — inclui edições ainda não-salvas,
   * pra prévia refletir exatamente o que ela está vendo. */
  formValues: PlatformSettingsFormInput;
}) {
  const platformSettings = buildPlatformSettingsFromForm(formValues);
  const lead = MOCK_LEAD;
  const proposal = MOCK_PROPOSAL;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100dvh-2rem)] overflow-y-auto sm:max-w-5xl">
        <DialogHeader>
          <DialogTitle>Prévia estética da proposta</DialogTitle>
          <DialogDescription>
            Renderização com dados fictícios (Dra. Exemplo · Clínica Exemplo) usando os valores
            ATUAIS do formulário — inclusive edições não-salvas. Serve só pra checar o visual. Pra
            ver uma proposta real, abra-a na página pública pelo link do lead.
          </DialogDescription>
        </DialogHeader>

        {/* `-mx-*` + border pra o ProposalPublicView ocupar largura total
            do modal (ele tem frame interno `max-w-6xl` que já centraliza).
            Mesmo padrão do ProposalPreviewModal real. */}
        <div className="-mx-4 border-y border-border bg-background sm:-mx-6">
          <ProposalPublicView
            proposal={proposal}
            platformSettings={platformSettings}
            lead={lead}
            mode="preview"
          />
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type="button" variant="outline" size="lg">
              Fechar prévia
            </Button>
          </DialogClose>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** Mapeia o shape flat do form (`{'contratada.razao_social': '...'}`)
 * pro shape tipado que o `ProposalPublicView` espera. Mesma estrutura
 * que o consumer público (`platform-settings.ts`) retorna — duplicação
 * consciente: aqui usamos valores do FORM (vivos), lá do BANCO. */
function buildPlatformSettingsFromForm(v: PlatformSettingsFormInput): PlatformSettings {
  const get = (key: string, fallback = ''): string => v[key] ?? fallback;
  return {
    contratada: {
      razao_social: get('contratada.razao_social'),
      cnpj: get('contratada.cnpj'),
      nome_fantasia: get('contratada.nome_fantasia'),
      cidade_uf: get('contratada.cidade_uf'),
      email_contato: get('contratada.email_contato'),
      responsavel_nome: get('contratada.responsavel_nome'),
      responsavel_cargo: get('contratada.responsavel_cargo'),
    },
    proposal_footer: {
      institutional_text: get('proposal.footer_institutional'),
    },
    proposal_copy: {
      hero_title: get('proposal.hero_title', 'Olá, é um prazer'),
      hero_intro_template: get('proposal.hero_intro_template'),
      about_us_title: get('proposal.about_us_title', 'Sobre nós'),
      about_us_paragraphs: get('proposal.about_us_paragraphs'),
      considerations_bullets: get('proposal.considerations_bullets'),
      post_consulting_terms_template: get('proposal.post_consulting_terms_template'),
    } satisfies ProposalCopySettings,
  };
}

// ── Mock data pra preview ──────────────────────────────────────────────
//
// Escolha deliberada: proposta COMBO (saas+consulting) com preço custom
// de SaaS + 6 parcelas da consultoria + SaaS grátis durante consultoria.
// Isso exercita 100% dos blocos visuais do ProposalPublicView:
//   - Hero (nome do destinatário, validade)
//   - Bloco 01 (hero_intro_template com tokens)
//   - Bloco 02 (about_us_paragraphs com tokens)
//   - Bloco 03 (escopo/notes) — preenchido
//   - Bloco 04 Consultoria
//   - Bloco 05 Plataforma SaaS
//   - Bloco 06 Cronograma de cobrança (só em combo)
//   - Bloco Considerações (bullets estáticos + dinâmicos)
//   - Footer com assinatura da contratada
//
// Se algum dia o ProposalPublicView ganhar novos blocos, atualizar esse
// mock pra cobrir o novo caminho e a Karla conseguir revisá-lo aqui.

const MOCK_LEAD = {
  full_name: 'Dra. Exemplo da Silva',
  clinic_name_guess: 'Clínica Exemplo',
};

const MOCK_PROPOSAL: LeadProposalSummary = {
  id: '00000000-0000-0000-0000-000000000000',
  proposal_type: 'saas_consulting',
  status: 'sent',
  plan_id: null,
  plan_key: 'pro',
  plan_name_pt_br: 'Pro',
  // SaaS
  discount_percent: 0,
  custom_price_brl: null,
  trial_days: 14,
  // Consultoria
  consulting_installments_count: 6,
  consulting_installment_brl: 3_000,
  consulting_duration_months: 6,
  consulting_weekly_hours: 5,
  consulting_extra_hour_brl: 350,
  consulting_scope_notes: null,
  consulting_modality: 'hibrido',
  consulting_payment_method: 'asaas_pix',
  // Combo
  saas_free_during_consulting: true,
  // Metadata
  valid_until: new Date(Date.now() + 14 * 86_400_000).toISOString(),
  notes:
    'Dra. Exemplo, esta é a prévia do escopo que a Karla escreve livremente pra cada lead — aqui usamos um texto fictício só pra mostrar como o bloco 03 fica renderizado.\n\nEste bloco aparece na proposta real sempre que há `notes` preenchido. A Karla consegue editar pela prévia individual do lead.',
  public_token: 'preview-token',
  sent_at: new Date().toISOString(),
  viewed_at: null,
  accepted_at: null,
  rejected_at: null,
  questioning_message: null,
  ai_context: null,
  ai_highlights: null,
  pre_negotiation_snapshot: null,
  created_at: new Date().toISOString(),
  updated_at: new Date().toISOString(),
};
